import { useState } from 'react';
import type { BugFilters, BugReport } from '../../lib/bugReportsApi';
import { useBugReports } from '../../hooks/useBugReports';
import { BugReportsFilters } from '../bugReports/BugReportsFilters';
import { BugReportsTable } from '../bugReports/BugReportsTable';
import { FadeIn, SkeletonTable } from '../ui/Skeleton';
import { useI18n } from '../../contexts/I18nContext';

const DEFAULT_FILTERS: BugFilters = {
  sort: 'createdAt',
  order: 'desc',
  page: 1,
  limit: 50,
};

/**
 * "Bug-uri raportate" — everything filed through the floating widget.
 *
 * The list body, rendered by BugReportsPage at /bug-reports. It began as a tab
 * in Settings, but that strip is twenty entries long and scrolls sideways, so
 * the list sat off the right edge where the admin who needed it never found
 * it. `/api/bug-reports` is `authorizeRole('ADMIN')` server-side, so the route
 * gate around this is navigation, not the security border.
 *
 * The list only. Opening a row is the page's business: it swaps this out for
 * the detail view, which needs the whole surface to lay itself out the way a
 * task does.
 */
export function BugReportsSettings({ onOpen }: { onOpen: (report: BugReport) => void }) {
  const { t } = useI18n();
  const [filters, setFilters] = useState<BugFilters>(DEFAULT_FILTERS);

  const { data, isLoading, isError, error } = useBugReports(filters);
  const reports = data?.data ?? [];
  const meta = data?.meta;

  // Any filter change resets to page 1 — page 3 of the old result set says
  // nothing about the new one.
  function patchFilters(patch: Partial<BugFilters>) {
    setFilters((prev) => ({ ...prev, ...patch, page: 1 }));
  }

  function handleSort(field: NonNullable<BugFilters['sort']>) {
    setFilters((prev) => ({
      ...prev,
      sort: field,
      order: prev.sort === field && prev.order === 'desc' ? 'asc' : 'desc',
      page: 1,
    }));
  }

  const filtered = Boolean(filters.q || filters.status || filters.priority);

  return (
    <div>
      <p className="text-sm text-gray-500">{t('bugReports.intro')}</p>

      <div className="mt-6">
        <BugReportsFilters
          filters={filters}
          onChange={patchFilters}
          onClear={() => setFilters(DEFAULT_FILTERS)}
        />
      </div>

      <div className="mt-6">
        {isLoading && <SkeletonTable rows={8} columns={6} />}
        {isError && (
          <p className="text-sm text-red-600">
            {t('bugReports.loadError')}
            {error instanceof Error ? `: ${error.message}` : ''}.
          </p>
        )}
        {!isLoading && !isError && (
          <FadeIn>
            <BugReportsTable
              reports={reports}
              sort={filters.sort ?? 'createdAt'}
              order={filters.order ?? 'desc'}
              onSort={handleSort}
              onOpen={onOpen}
              filtered={filtered}
            />

            {meta && meta.totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
                <span>
                  {t('bugReports.pageOf', {
                    page: meta.page,
                    totalPages: meta.totalPages,
                    total: meta.total,
                  })}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={meta.page <= 1}
                    onClick={() => setFilters((p) => ({ ...p, page: (p.page ?? 1) - 1 }))}
                    className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
                  >
                    {t('bugReports.previous')}
                  </button>
                  <button
                    type="button"
                    disabled={meta.page >= meta.totalPages}
                    onClick={() => setFilters((p) => ({ ...p, page: (p.page ?? 1) + 1 }))}
                    className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
                  >
                    {t('bugReports.next')}
                  </button>
                </div>
              </div>
            )}
          </FadeIn>
        )}
      </div>
    </div>
  );
}
